// src/pages/DashboardPage.jsx
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { ordersAPI, ridersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { StatusBadge, LoadingOverlay, EmptyState } from '../components/UI';
import moment from 'moment';
import { usePolling } from '../hooks/usePolling';

function buildChartData(orders) {
  return Array.from({ length: 7 }, (_, i) => {
    const day = moment().subtract(6 - i, 'days');
    const dayOrders = orders.filter(o => moment(o.created_at).isSame(day, 'day'));
    return {
      day: day.format('ddd'),
      total: dayOrders.length,
      delivered: dayOrders.filter(o => o.status === 'Delivered').length,
      failed: dayOrders.filter(o => o.status === 'Failed').length,
    };
  });
}

function buildStatusPie(orders) {
  const counts = {};
  orders.forEach(o => { counts[o.status] = (counts[o.status] || 0) + 1; });
  const colors = {
    'Pending': '#94a3b8', 'Assigned': '#3b82f6', 'Picked Up': '#f59e0b',
    'In Transit': '#8b5cf6', 'Delivered': '#22c55e', 'Failed': '#ef4444',
  };
  return Object.entries(counts).map(([name, value]) => ({ name, value, fill: colors[name] || '#94a3b8' }));
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--primary)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--white)', boxShadow: 'var(--shadow-lg)' }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map(p => <div key={p.dataKey} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></div>)}
    </div>
  );
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('today'); // 'today' | 'all'

  const fetchData = useCallback(async () => {
    try {
      const [o, r] = await Promise.all([ordersAPI.getAll(), ridersAPI.getAll()]);
      setOrders(o || []);
      setRiders(r || []);
    } catch { }
    finally { setLoading(false); }
  }, []);

  usePolling(fetchData, 10000);

  if (loading) return <LoadingOverlay message="Loading dashboard…" />;

  const today = moment().format('YYYY-MM-DD');
  const todayOrders = orders.filter(o => moment(o.created_at).format('YYYY-MM-DD') === today);

  const scopedOrders = viewMode === 'today' ? todayOrders : orders;

  const active = scopedOrders.filter(o => !['Delivered', 'Failed'].includes(o.status));
  const delivered = scopedOrders.filter(o => o.status === 'Delivered');
  const failed = scopedOrders.filter(o => o.status === 'Failed');
  const successRate = scopedOrders.length ? Math.round((delivered.length / scopedOrders.length) * 100) : 0;

  const isAdmin = user?.role === 'Admin';

  const paidOrders = scopedOrders.filter(o => o.payment_status === 'Paid');
  const unpaidOrders = scopedOrders.filter(o => o.payment_status !== 'Paid' && parseFloat(o.delivery_fee || 0) > 0);

  const totalRevenue = scopedOrders.reduce((s, o) => s + parseFloat(o.delivery_fee || 0), 0);
  const paidRevenue = paidOrders.reduce((s, o) => s + parseFloat(o.delivery_fee || 0), 0);
  const unpaidRevenue = unpaidOrders.reduce((s, o) => s + parseFloat(o.delivery_fee || 0), 0);

  const cashOrders = scopedOrders.filter(o => o.payment_method === 'Cash');
  const transferOrders = scopedOrders.filter(o => o.payment_method === 'Transfer');
  const cashRevenue = cashOrders.reduce((s, o) => s + parseFloat(o.delivery_fee || 0), 0);
  const transferRevenue = transferOrders.reduce((s, o) => s + parseFloat(o.delivery_fee || 0), 0);

  const chartData = buildChartData(orders);
  const pieData = buildStatusPie(scopedOrders);

  const recentOrders = [...scopedOrders]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, viewMode === 'today' ? 20 : 8);

  return (
    <div className="animate-fade-in">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-sub">{moment().format('dddd, MMMM D YYYY')} · Overview of all operations</p>
        </div>

        <div style={{ display: 'flex', background: 'var(--gray-100)', borderRadius: 10, padding: 4, gap: 2 }}>
          <button
            onClick={() => setViewMode('today')}
            style={{
              padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-mono)',
              background: viewMode === 'today' ? 'var(--primary)' : 'transparent',
              color: viewMode === 'today' ? 'var(--white)' : 'var(--gray-500)',
              transition: 'all 0.15s',
            }}
          >
            Today
          </button>
          <button
            onClick={() => setViewMode('all')}
            style={{
              padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-mono)',
              background: viewMode === 'all' ? 'var(--primary)' : 'transparent',
              color: viewMode === 'all' ? 'var(--white)' : 'var(--gray-500)',
              transition: 'all 0.15s',
            }}
          >
            All Time
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 16, marginTop: -8 }}>
        <span style={{
          fontSize: 12, fontWeight: 600, color: viewMode === 'today' ? '#3b82f6' : 'var(--gray-400)',
          fontFamily: 'var(--font-mono)',
        }}>
          {viewMode === 'today'
            ? `Showing ${todayOrders.length} order${todayOrders.length !== 1 ? 's' : ''} from today — resets at midnight`
            : `Showing all ${orders.length} orders since launch`}
        </span>
      </div>

      <div className="stat-grid" style={{ marginBottom: 24 }}>
        {[
          { label: viewMode === 'today' ? "Today's Orders" : 'Total Orders', value: scopedOrders.length, cls: 'accent', sub: viewMode === 'today' ? moment().format('MMM D') : `${todayOrders.length} today` },
          { label: 'Active Deliveries', value: active.length, cls: 'info', sub: 'In progress' },
          { label: 'Delivered', value: delivered.length, cls: 'success', sub: `${successRate}% success rate` },
          { label: 'Failed', value: failed.length, cls: 'danger', sub: 'Need follow-up' },
        ].map((s, i) => (
          <div key={s.label} className={`stat-card ${s.cls} animate-fade-up delay-${i + 1}`}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {isAdmin && (
        <>
          <div className="stat-grid" style={{ marginBottom: 16 }}>
            <div className="stat-card accent animate-fade-up delay-1">
              <div className="stat-label">{viewMode === 'today' ? "Today's Revenue" : 'Total Revenue'}</div>
              <div className="stat-value">₦{totalRevenue.toLocaleString()}</div>
              <div className="stat-sub">{scopedOrders.length} orders</div>
            </div>

            <div className="stat-card success animate-fade-up delay-2">
              <div className="stat-label">Paid Revenue</div>
              <div className="stat-value">₦{paidRevenue.toLocaleString()}</div>
              <div className="stat-sub">{paidOrders.length} paid orders</div>
            </div>

            <div className="stat-card warning animate-fade-up delay-3" style={{ position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #f59e0b, #ef4444)' }} />
              <div className="stat-label">Unpaid / Outstanding</div>
              <div className="stat-value">₦{unpaidRevenue.toLocaleString()}</div>
              <div className="stat-sub">{unpaidOrders.length} pending payment</div>
            </div>
          </div>

          <div className="stat-grid" style={{ marginBottom: 24 }}>
            <div className="stat-card warning animate-fade-up delay-1">
              <div className="stat-label">Cash Orders</div>
              <div className="stat-value">{cashOrders.length}</div>
              <div className="stat-sub">₦{cashRevenue.toLocaleString()}</div>
            </div>

            <div className="stat-card info animate-fade-up delay-2">
              <div className="stat-label">Transfer Orders</div>
              <div className="stat-value">{transferOrders.length}</div>
              <div className="stat-sub">₦{transferRevenue.toLocaleString()}</div>
            </div>

            <div className="stat-card accent animate-fade-up delay-3">
              <div className="stat-label">Active Riders</div>
              <div className="stat-value">{riders.filter(r => r.status !== 'Offline').length}</div>
              <div className="stat-sub">{riders.filter(r => r.status === 'Busy').length} on delivery</div>
            </div>
          </div>
        </>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, marginBottom: 24 }}>
        <div className="card animate-fade-up delay-3">
          <div className="card-header"><span className="card-title">Deliveries — Last 7 Days</span></div>
          <div className="card-body" style={{ paddingTop: 8 }}>
            {orders.length === 0 ? <EmptyState title="No data yet" subtitle="Orders will appear here" /> : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-100)" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: 'var(--gray-400)', fontFamily: 'var(--font-mono)' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: 'var(--gray-400)', fontFamily: 'var(--font-mono)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="delivered" name="Delivered" fill="var(--success)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="failed" name="Failed" fill="var(--danger)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="total" name="Total" fill="var(--accent)" radius={[4, 4, 0, 0]} opacity={0.3} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="card animate-fade-up delay-4">
          <div className="card-header"><span className="card-title">{viewMode === 'today' ? "Today's Orders by Status" : 'Orders by Status'}</span></div>
          <div className="card-body" style={{ paddingTop: 8 }}>
            {pieData.length === 0 ? <EmptyState title="No data" subtitle="Orders will appear here" /> : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="45%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value">
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip formatter={(val, name) => [val, name]} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="card animate-fade-up delay-5">
        <div className="card-header">
          <span className="card-title">{viewMode === 'today' ? "Today's Orders" : 'Recent Orders'}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/orders')}>View All →</button>
        </div>
        {recentOrders.length === 0 ? (
          <EmptyState
            title={viewMode === 'today' ? 'No orders today yet' : 'No orders yet'}
            subtitle={viewMode === 'today' ? 'New orders will appear here as they come in' : 'Create your first order to get started'}
          />
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Order ID</th>
                <th>Package</th>
                <th>Receiver</th>
                <th>Address</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Created</th>
              </tr></thead>
              <tbody>
                {recentOrders.map(o => (
                  <tr key={o.id} onClick={() => navigate(`/orders/${o.id}`)} style={{ cursor: 'pointer' }}>
                    <td className="td-mono">#{o.id}</td>
                    <td className="td-mono">{o.package_id}</td>
                    <td style={{ fontWeight: 600, color: 'var(--gray-900)' }}>{o.receiver_name}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.address}</td>
                    <td><StatusBadge status={o.status} /></td>
                    <td>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99,
                        background: o.payment_status === 'Paid' ? 'rgba(34,197,94,.15)' : 'rgba(245,158,11,.15)',
                        color: o.payment_status === 'Paid' ? '#22c55e' : '#f59e0b',
                        border: `1px solid ${o.payment_status === 'Paid' ? 'rgba(34,197,94,.3)' : 'rgba(245,158,11,.3)'}`,
                      }}>
                        {o.payment_status === 'Paid' ? '✓ Paid' : ' Unpaid'}
                      </span>
                    </td>
                    <td className="td-mono" style={{ color: 'var(--gray-400)', fontSize: 12 }}>
                      {moment(o.created_at).format('MMM D, h:mm A')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}